/**
 * LanguageSelector - Searchable multi-select dropdown for language selection.
 *
 * Features:
 * - Single or multi-select mode
 * - Search/filter support
 * - Auto Detect option
 * - Popular languages section
 * - Theme-aware styling via CSS variables
 *
 * @file LanguageSelector.jsx
 */
import React, { useState, useMemo, useRef, useEffect, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
// import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, POPULAR_LANGUAGES } from '../data/supportedLanguages';
import { X, ChevronDown, Check, Search } from 'lucide-react';
import styles from './LanguageSelector.module.css';

/**
 * @param {Object} props
 * @param {string[]} props.selectedLanguages - Array of selected language codes
 * @param {function} props.onSelectionChange - Callback when selection changes
 * @param {Object} props.theme - Theme object (deprecated, uses CSS variables)
 * @param {string} props.placeholder - Placeholder text for search input
 * @param {boolean} props.singleSelect - If true, only allow single selection
 * @param {boolean} props.disabled - If true, disable the selector
 * @param {boolean} props.includeAutoDetect - If true, include "Auto Detect" as first option (value: '')
 * @param {string} props.autoDetectLabel - Label for auto detect option (default: 'Auto Detect')
 * @param {boolean} props.error - If true, show error state
 */
const LanguageSelector = ({
    selectedLanguages,
    onSelectionChange,
    placeholder,
    singleSelect = false,
    disabled = false,
    includeAutoDetect = false,
    autoDetectLabel,
    error = false
}) => {
    // Placeholder for missing translation hook
    const t = (key, defaultValue) => {
        const defaults = {
            'language.searchLanguages': 'Search languages...',
            'language.autoDetect': 'Auto Detect',
            'language.popularLanguages': 'Popular Languages',
            'language.noMatchingLanguages': 'No matching languages found',
            'language.allLanguages': 'All Languages'
        };
        return defaultValue || defaults[key] || key;
    };

    // Use translated defaults if props not provided
    const effectivePlaceholder = placeholder || t('language.searchLanguages');
    const effectiveAutoDetectLabel = autoDetectLabel || t('language.autoDetect', 'Auto Detect');

    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const selectedLanguagesRef = useRef(selectedLanguages);

    // Keep ref in sync synchronously to avoid race conditions with onBlur
    useLayoutEffect(() => {
        selectedLanguagesRef.current = selectedLanguages;
    }, [selectedLanguages]);

    // Sync search query with selected language in singleSelect mode
    useEffect(() => {
        if (singleSelect) {
            if (selectedLanguages.length > 0) {
                const selectedCode = selectedLanguages[0];
                // Handle Auto Detect (empty string)
                if (selectedCode === '' && includeAutoDetect) {
                    setSearchQuery(effectiveAutoDetectLabel);
                } else {
                    const lang = SUPPORTED_LANGUAGES.find(l => l.code === selectedCode);
                    if (lang) {
                        setSearchQuery(lang.name);
                    }
                }
            } else if (includeAutoDetect) {
                // No selection but Auto Detect is enabled - show Auto Detect
                setSearchQuery(effectiveAutoDetectLabel);
            }
        }
    }, [selectedLanguages, singleSelect, includeAutoDetect, effectiveAutoDetectLabel]);

    // Filter and sort languages
    const filteredLanguages = useMemo(() => {
        const query = searchQuery.toLowerCase().trim();

        let filtered = SUPPORTED_LANGUAGES;

        // FIX: If the search query exactly matches the currently selected language (in single select mode),
        // we assume the user hasn't started searching yet and wants to see all options.
        // This fixes the UX issue where opening the dropdown only showed the selected language.
        const isExactMatch = singleSelect &&
            selectedLanguages.length === 1 &&
            ((searchQuery === effectiveAutoDetectLabel && selectedLanguages[0] === '') || // Auto Detect case
                SUPPORTED_LANGUAGES.find(l => l.code === selectedLanguages[0])?.name === searchQuery);

        if (query && !isExactMatch) {
            filtered = SUPPORTED_LANGUAGES.filter(lang =>
                lang.name.toLowerCase().includes(query) ||
                lang.code.toLowerCase().includes(query)
            );
        } else {
            // Show all languages if no query or if it's an exact match
            // Clone array to avoid mutating the original SUPPORTED_LANGUAGES during sort
            filtered = [...SUPPORTED_LANGUAGES];
        }

        // Sort: popular languages first, then alphabetical (only when no search query or exact match)
        if (!query || isExactMatch) {
            filtered.sort((a, b) => {
                const aPopular = POPULAR_LANGUAGES.includes(a.code);
                const bPopular = POPULAR_LANGUAGES.includes(b.code);
                if (aPopular && !bPopular) return -1;
                if (!aPopular && bPopular) return 1;
                return a.name.localeCompare(b.name);
            });
        }

        return filtered;
    }, [searchQuery, singleSelect, selectedLanguages, effectiveAutoDetectLabel]);

    const handleToggleLanguage = (code) => {
        if (singleSelect) {
            onSelectionChange([code]);
            setIsOpen(false);
            // Search query update handled by useEffect
        } else {
            if (selectedLanguages.includes(code)) {
                onSelectionChange(selectedLanguages.filter(c => c !== code));
            } else {
                onSelectionChange([...selectedLanguages, code]);
            }
            // Keep open for multi-select convenience, refocus input
            inputRef.current?.focus();
        }
    };

    const handleRemoveLanguage = (code, e) => {
        e.stopPropagation();
        onSelectionChange(selectedLanguages.filter(c => c !== code));
    };

    const handleInputClick = () => {
        if (disabled) return;
        setIsOpen(true);
        inputRef.current?.focus();
    };

    const handleCaretClick = (e) => {
        e.stopPropagation(); // Stop it from bubbling to inputWrapper click
        if (disabled) return;

        // Toggle state
        setIsOpen(prev => !prev);

        // If opening, focus input
        if (!isOpen) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };

    const handleClearInput = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setSearchQuery('');
        setIsOpen(true);
        inputRef.current?.focus();
    };

    const isMouseDownRef = useRef(false);

    return (
        <div
            ref={containerRef}
            className={styles.container}
            onMouseDownCapture={() => { isMouseDownRef.current = true; }}
            onMouseUpCapture={() => { isMouseDownRef.current = false; }}
        >
            {/* Input wrapper with pills and search */}
            <div
                className={styles.inputWrapper}
                onClick={handleInputClick}
                data-open={isOpen}
                data-disabled={disabled}
                data-error={error}
            >
                {/* Search Icon if empty and inactive */}
                {selectedLanguages.length === 0 && !searchQuery && (
                    <Search size={14} className={styles.searchIcon} />
                )}

                {/* Selected language pills (hidden in singleSelect) */}
                {!singleSelect && (
                    <div className={styles.pillContainer}>
                        {selectedLanguages.map(code => {
                            const lang = SUPPORTED_LANGUAGES.find(l => l.code === code);
                            return (
                                <span key={code} className={styles.pill}>
                                    {lang?.name || code}
                                    <button
                                        className={styles.pillRemove}
                                        onClick={(e) => handleRemoveLanguage(code, e)}
                                        title="Remove"
                                        type="button"
                                    >
                                        <X size={10} strokeWidth={3} />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                )}

                {/* Search input */}
                <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => {
                        if (!disabled) {
                            setIsOpen(true);
                            if (singleSelect) {
                                inputRef.current?.select();
                            }
                        }
                    }}
                    onBlur={() => {
                        // Prevent reset if clicking inside the dropdown (e.g. scrollbar or options)
                        if (isMouseDownRef.current) return;

                        // Reset query to selected language if no new selection made (delay to allow click to register)
                        if (singleSelect) {
                            setTimeout(() => {
                                // Double check interaction state in timeout
                                if (isMouseDownRef.current) return;

                                const selectedCode = selectedLanguagesRef.current[0];
                                // Handle Auto Detect (empty string or no selection)
                                if ((selectedCode === '' || selectedLanguagesRef.current.length === 0) && includeAutoDetect) {
                                    setSearchQuery(effectiveAutoDetectLabel);
                                } else {
                                    const lang = SUPPORTED_LANGUAGES.find(l => l.code === selectedCode);
                                    if (lang) {
                                        setSearchQuery(lang.name);
                                    }
                                }
                            }, 200);
                        }
                    }}
                    placeholder={singleSelect && selectedLanguages.length > 0 ? '' : (selectedLanguages.length === 0 ? effectivePlaceholder : '')}
                    className={styles.input}
                    disabled={disabled}
                />

                {/* Clear button for single select */}
                {singleSelect && searchQuery && !disabled && (
                    <button
                        onMouseDown={handleClearInput}
                        className={styles.clearButton}
                        title="Clear"
                        type="button"
                    >
                        <X size={16} />
                    </button>
                )}

                {/* Dropdown chevron */}
                <button
                    className={styles.chevronBtn}
                    onClick={handleCaretClick}
                    type="button"
                    tabIndex={-1}
                    data-open={isOpen}
                >
                    <ChevronDown size={16} />
                </button>
            </div>

            {/* Dropdown list */}
            {isOpen && (
                <div className={styles.dropdown}>
                    {/* Auto Detect option - shown first when enabled and matches search */}
                    {includeAutoDetect && (
                        !searchQuery || effectiveAutoDetectLabel.toLowerCase().includes(searchQuery.toLowerCase())
                    ) && (
                            (() => {
                                const isAutoDetectSelected = selectedLanguages.includes('') || selectedLanguages.length === 0;
                                return (
                                    <div
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            handleToggleLanguage('');
                                        }}
                                        className={`${styles.option} ${styles.optionAutoDetect} ${isAutoDetectSelected ? styles.optionSelected : ''}`}
                                    >
                                        <span style={{ fontWeight: 500 }}>{effectiveAutoDetectLabel}</span>
                                        {isAutoDetectSelected && <Check size={16} className={styles.checkIcon} />}
                                    </div>
                                );
                            })()
                        )}

                    {!searchQuery && filteredLanguages.length > 0 && (
                        <div className={styles.sectionLabel}>{t('language.popularLanguages')}</div>
                    )}

                    {filteredLanguages.length === 0 && !(includeAutoDetect && effectiveAutoDetectLabel.toLowerCase().includes(searchQuery.toLowerCase())) ? (
                        <div className={styles.noResults}>
                            <Search size={24} className={styles.noResultsIcon} />
                            <span>{t('language.noMatchingLanguages')}</span>
                        </div>
                    ) : (
                        filteredLanguages.map((lang, index) => {
                            const isSelected = selectedLanguages.includes(lang.code);
                            const showDivider = !searchQuery && index === POPULAR_LANGUAGES.length;

                            return (
                                <div key={lang.code}>
                                    {showDivider && (
                                        <div className={`${styles.sectionLabel} ${styles.sectionLabelTop}`}>
                                            {t('language.allLanguages')}
                                        </div>
                                    )}
                                    <div
                                        onMouseDown={(e) => {
                                            e.preventDefault(); // Prevent input blur
                                            handleToggleLanguage(lang.code);
                                        }}
                                        className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                                    >
                                        <span>{lang.name}</span>
                                        {isSelected && <Check size={16} className={styles.checkIcon} />}
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}
        </div>
    );
};

LanguageSelector.propTypes = {
    selectedLanguages: PropTypes.arrayOf(PropTypes.string).isRequired,
    onSelectionChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    singleSelect: PropTypes.bool,
    disabled: PropTypes.bool,
    includeAutoDetect: PropTypes.bool,
    autoDetectLabel: PropTypes.string,
    error: PropTypes.bool,
};

export default LanguageSelector;
